import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { ORDER_STATUS, ORDER_STATUS_OPTIONS, getOrderStatusChartColor } from '../../../constants/order';

echarts.use([BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

// Order Fulfillment Pipeline 7 根柱子的展示顺序，与 Order List 页面「Order Status」筛选下拉框顺序保持一致
const PIPELINE_STATUS_ORDER = [
  ORDER_STATUS.CREATED,
  ORDER_STATUS.IN_PROGRESS,
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.OUTBOUND_COMPLETED,
  ORDER_STATUS.IN_DELIVERY,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.RETURN,
];

/**
 * Module 2: Real-time Operations & Alerts
 * Order Fulfillment Pipeline (7 bars，按 wms_order.status 7 种 OrderStatus 枚举值统计) + Online/Offline Pickers Pie
 */
const RealtimeOperations = ({ data, summaryData }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const { orderStatusCounts } = data;

  // 后端按 wms_order.status 分组统计当日各状态订单数，直接对应 OrderStatus 枚举 6 个值，无需前端二次计算
  const pipelineData = PIPELINE_STATUS_ORDER.map((statusValue) => {
    const option = ORDER_STATUS_OPTIONS.find((opt) => opt.value === statusValue);
    return {
      statusValue,
      label: t(option?.label || statusValue),
      value: orderStatusCounts?.[statusValue] ?? 0,
      color: getOrderStatusChartColor(statusValue),
    };
  });

  // Vertical bar chart - 6 categories (Order Status)
  const barChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => `${params[0].name}: <b>${params[0].value}</b>`,
    },
    grid: { left: 50, right: 30, top: 30, bottom: 60 },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 11 },
      splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } },
    },
    xAxis: {
      type: 'category',
      data: pipelineData.map((item) => item.label),
      axisLabel: { fontSize: 11, fontWeight: 'bold', color: '#333', interval: 0 },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
    },
    series: [{
      type: 'bar',
      barWidth: 40,
      data: pipelineData.map((item) => ({
        value: item.value,
        itemStyle: {
          color: item.color,
          borderRadius: [6, 6, 0, 0],
        },
      })),
      label: {
        show: true,
        position: 'top',
        formatter: '{c}',
        fontSize: 13,
        fontWeight: 'bold',
        color: '#333',
      },
    }],
  };

  // Online/Offline Pickers pie chart
  const onlinePickers = summaryData?.onlinePickers || 0;
  const totalPickers = summaryData?.totalPickers || 0;
  const offlinePickers = totalPickers - onlinePickers;

  const onlinePercent = totalPickers > 0 ? Math.round((onlinePickers / totalPickers) * 100) : 0;

  const pickersPieOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#eee',
      borderWidth: 1,
      textStyle: { color: '#333', fontSize: 13 },
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-radius: 8px;',
    },
    legend: { show: false },
    series: [
      // Outer decorative ring (very thin, light gray)
      {
        type: 'pie',
        radius: ['62%', '64%'],
        center: ['50%', '45%'],
        silent: true,
        label: { show: false },
        labelLine: { show: false },
        data: [{ value: 1, itemStyle: { color: '#f0f0f0' } }],
      },
      // Main donut
      {
        type: 'pie',
        radius: ['42%', '58%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 3,
          borderRadius: 6,
        },
        label: {
          show: true,
          position: 'center',
          formatter: () => `{num|${onlinePercent}%}\n{desc|${t('dashboard.nationwide.onlineRate')}}`,
          rich: {
            num: { fontSize: 26, fontWeight: 700, color: '#36CFC9', lineHeight: 32, fontFamily: '-apple-system, "SF Pro Display", sans-serif' },
            desc: { fontSize: 12, color: '#999', lineHeight: 20, fontFamily: '-apple-system, "SF Pro Text", sans-serif' },
          },
        },
        emphasis: {
          scale: true,
          scaleSize: 4,
          itemStyle: { shadowBlur: 16, shadowColor: 'rgba(0,0,0,0.12)' },
        },
        data: [
          {
            value: onlinePickers,
            name: t('dashboard.nationwide.online'),
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
                { offset: 0, color: '#36CFC9' },
                { offset: 1, color: '#13C2C2' },
              ]),
            },
          },
          {
            value: offlinePickers,
            name: t('dashboard.nationwide.offline'),
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
                { offset: 0, color: '#FFA39E' },
                { offset: 1, color: '#FF7875' },
              ]),
            },
          },
        ],
      },
    ],
  };

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', minWidth: 0 }}>
      {/* 7. Order Fulfillment Pipeline */}
      <Card
        title={t('dashboard.nationwide.orderFulfillmentPipeline')}
        style={{ flex: 3, minWidth: 0, overflow: 'hidden' }}
        styles={{ body: { padding: '8px 16px' } }}
      >
        <ReactEChartsCore
          echarts={echarts}
          option={barChartOption}
          style={{ height: 190 }}
          opts={{ renderer: 'canvas' }}
        />
      </Card>

      {/* 8. Online / Offline Pickers */}
      <Card
        title={t('dashboard.nationwide.totalPickersStatus')}
        extra={(
          <Tooltip title={t('dashboard.nationwide.totalPickersStatusTooltip')}>
            <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
          </Tooltip>
        )}
        style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        styles={{ body: { padding: '8px 16px' } }}
      >
        <ReactEChartsCore
          echarts={echarts}
          option={pickersPieOption}
          style={{ height: 190 }}
          opts={{ renderer: 'canvas' }}
        />
        <div style={{ display: 'flex', justifyContent: 'center', gap: 28, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: 'linear-gradient(135deg, #36CFC9, #13C2C2)', display: 'inline-block' }} />
            <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>{t('dashboard.nationwide.online')}</span>
            <span style={{ fontSize: 15, color: '#13C2C2', fontWeight: 700 }}>{onlinePickers}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: 'linear-gradient(135deg, #FFA39E, #FF7875)', display: 'inline-block' }} />
            <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>{t('dashboard.nationwide.offline')}</span>
            <span style={{ fontSize: 15, color: '#FF7875', fontWeight: 700 }}>{offlinePickers}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RealtimeOperations;
